import React, { useEffect, useRef } from 'react';
import {BuyingOption, DeliveryPromiseTime, MerchantBusinessHours, PriceType, StateCode, VisitType} from 'state/types';
import { Merchant } from 'state/types/merchants';
import DeliveryPromiseUtils from 'state/utils/DeliveryPromiseUtils';

export interface BuyingOptionWidgetProps {
    readonly selectedStateCode: StateCode
    readonly visitType: VisitType
    readonly buyingOption: BuyingOption;
    readonly merchant: Merchant;
    readonly deliveryPromiseTime?: DeliveryPromiseTime;
    readonly selected: boolean
    readonly onClickCallback: (buyingOption: BuyingOption, visitType: VisitType, selectedStateCode: StateCode) => void
}

const renderCountDownTimeText = (minutes: number): (string | undefined) => {
    if (minutes >= 60) {
        return
    }
    const minutesInt = Math.round(minutes)
    if (minutesInt === 0) {
        // this should never happen because clinic is closed...
        return;
    }
    return `${minutesInt} minute${minutesInt > 1 ? 's' : ''}`
}

const renderBusinessHourText = (businessHours: MerchantBusinessHours, isClinicClose: boolean = false) => {
    if (businessHours.operatingStrategies.includes('NOT_AVAILABLE_DURING_NON_BUSINESS_HOURS')) {
        const startLocalTimeText = DeliveryPromiseUtils.buildBusinessHourLocalTime(businessHours.startOffsetTime, false)
        const endLocalTimeText = DeliveryPromiseUtils.buildBusinessHourLocalTime(businessHours.endOffsetTime, true)
        if (!startLocalTimeText || !endLocalTimeText) {
            return ""
        }
        if (isClinicClose) {
            return `${startLocalTimeText} to ${endLocalTimeText}`
        }
        // Keep available text when clinic is open per https://issues.amazon.com/issues/KATARA-SX-5463.
        return `(available ${startLocalTimeText} to ${endLocalTimeText})`
    } else {
        // display nothing if unknown strategy found.
        // we treat AVAILABLE_DURING_NON_BUSINESS_HOURS as 24 hours/day even if provider has business hour
        // display nothing if provider is AVAILABLE_DURING_NON_BUSINESS_HOURS to match async behavior
        return ""
    }
}

const renderOfferPrice = (price: PriceType) => {
    const pricesArr = price.amount.toFixed(2).split('.');
    const currencySymbol = price.currencySymbol ?? "";
    return (
        <span className="a-price" data-a-size="xl" data-a-color="base">
            <span aria-hidden="true">
                <span className="a-price-symbol">{currencySymbol}</span>
                <span className="a-price-whole">{pricesArr[0]}<span className="a-price-decimal"></span>
                </span>
                <span className="a-price-fraction">{pricesArr[1]}</span>
            </span>
        </span>
    );
}

const BuyingOptionWidget = ({buyingOption, visitType, merchant, selected, deliveryPromiseTime, selectedStateCode, onClickCallback}: BuyingOptionWidgetProps) => {
    const radioRef = useRef();
    const isClinicOpen = deliveryPromiseTime?.operatingStatus?.isClinicOpen ?? true
    const visitTypeLowerCase = visitType.toLowerCase()

    useEffect(() => {
        const radio: any = radioRef.current;
        if (!!radio) {
            if (isClinicOpen) {
                radio.enable()
                if (selected) {
                    radio.check();
                } else {
                    radio.uncheck();
                }
            } else {
                radio.uncheck();
                radio.disable()
            }
            radio.render()
        }
    }, [selected, deliveryPromiseTime])

    const generateDeliveryPromiseMessage = () => {
        const businessHoursText = !!deliveryPromiseTime?.businessHours
            ? renderBusinessHourText(deliveryPromiseTime.businessHours, !isClinicOpen)
            : undefined
        if (!isClinicOpen) {
            return <><span className='clinic-closed'>{'This clinic is closed. '}</span><span>{!!businessHoursText ? `Business hours: ${businessHoursText}` : ''}</span></>
        }
        const responseTimeText = !!deliveryPromiseTime?.responseTime?.latestResponseInSecond
            ? DeliveryPromiseUtils.buildBuyingOptionResponseTimeText(deliveryPromiseTime?.responseTime?.latestResponseInSecond)
            : undefined
        if (!responseTimeText) {
            return <></>
        }
        let deliveryPromiseLabel;
        if ("synchronous" === visitTypeLowerCase) {
            deliveryPromiseLabel = 'typical wait time'
        } else {
            deliveryPromiseLabel = 'typical response time'
        }
        let countDownInMinuteText: string | undefined = undefined
        if (deliveryPromiseTime?.operatingStatus) {
            const operatingStatus = deliveryPromiseTime?.operatingStatus
            if (operatingStatus.isClinicOpen) {
                countDownInMinuteText = renderCountDownTimeText(operatingStatus.toggleInMinute)
            }
        }
        return <>
            {!!countDownInMinuteText
                && <span>
                    <span>{'Order within '}</span>
                    <strong className='count-down-time'>{countDownInMinuteText}</strong>
                    <span>{' to be seen today. '}</span>
                </span>}
            <strong>{responseTimeText}</strong>&#32;<span>{deliveryPromiseLabel}</span>{!!businessHoursText ? ` ${businessHoursText}` : ''}
        </>
    }

    const deliveryPromiseSection = generateDeliveryPromiseMessage()

    return (<pui-section fullWidth onClick={() => { isClinicOpen && onClickCallback(buyingOption, visitType, selectedStateCode)}}>
        <pui-section flowDirection="horizontal" class="buying-option-box">
            <pui-section-column flexGrid="10">
                <pui-radio-button
                    value={buyingOption.obfuscatedMerchantId}
                    label=" " // Don't remove this blank label, see https://t.corp.amazon.com/P84270613
                    ref={radioRef}
                />
            </pui-section-column>
            <pui-section-column flexGrid="90" >
                <pui-section flowDirection="horizontal" >
                    <pui-section-column flexGrid="60">
                        <img className='merchant-logo' src={merchant.logoUrl} />
                    </pui-section-column>
                    <pui-section-column flexGrid="40">
                        <pui-section flowDirection="horizontal" mainAxisArrangement="end" class="price">
                            {renderOfferPrice(buyingOption.priceToPay)}
                        </pui-section>
                    </pui-section-column>
                </pui-section>
                {deliveryPromiseSection}
                <pui-section flowDirection="horizontal" spacingTop="mini">
                    <pui-link external href={merchant.merchantPageLink.url} text={merchant.merchantPageLink.text}></pui-link>
                </pui-section>
            </pui-section-column>
        </pui-section>
    </pui-section>)
}

export default BuyingOptionWidget;